import { Spinner, Toolbar, ToolbarButton } from '@fluentui/react-components';
import { useAppDispatch, useSelector } from '../../store/hooks';
import { useHealthDashboardPageStyles } from './HealthDashboardPage.styles';
import { useCallback, useEffect, useState } from 'react';
import {
    loadHealthInfo,
    setServiceStatuses,
} from '../../store/features/healthDashboard/healthDashboardSlice';
import { app } from '@microsoft/teams-js';
import { useTranslation } from 'react-i18next';
import DepartureBoardTile from './tiles/DepartureBoardTile/DepartureBoardTile';
import WelcomeCardTile from './tiles/WelcomeCardTile/WelcomeCardTile';
import CrewTile from './tiles/CrewTile/CrewTile';
import ConnectionsTile from './tiles/ConnectionsTile/ConnectionsTile';
import BaggageTile from './tiles/BaggageTile/BaggageTile';
import { downloadFailureReport } from '../../store/features/healthDashboard/healthDashboardApi';
import { downloadBlob } from '../../utils/blobUtils';
import { ArrowDownloadRegular } from '@fluentui/react-icons';

const HealthDashboardPage: React.FC = () => {
    const styles = useHealthDashboardPageStyles();

    const { loadStatus, healthInfo } = useSelector((x) => x.healthDashboard);
    const dispatch = useAppDispatch();

    const { t } = useTranslation();

    useEffect(() => {
        dispatch(loadHealthInfo())
            .unwrap()
            .then((x) => {
                const srv = x.services;

                dispatch(
                    setServiceStatuses({
                        heathrowApi: {
                            id: 'heathrowApi',
                            label: t(
                                'FeatureService_HeathrowApi',
                                'Heathrow API'
                            ),
                            status: srv.heathrowApi,
                        },
                        gatwickApi: {
                            id: 'gatwickApi',
                            label: t(
                                'FeatureService_GatwickApi',
                                'Gatwick API'
                            ),
                            status: srv.gatwickApi,
                        },
                        crewRoster: {
                            id: 'crewRoster',
                            label: t(
                                'FeatureService_CrewRoster',
                                'Crew Roster'
                            ),
                            status: srv.crewRoster,
                        },
                        functionApp: {
                            id: 'functionApp',
                            label: t(
                                'FeatureService_FunctionApp',
                                'Function App'
                            ),
                            status: srv.functionApp,
                        },
                        grpcServer: {
                            id: 'grpcServer',
                            label: t(
                                'FeatureService_GrpcServer',
                                'GRPC Server'
                            ),
                            status: srv.grpcServer,
                        },
                    })
                );
            });
    }, [dispatch, t]);

    useEffect(() => {
        if (loadStatus === 'idle') {
            app.notifySuccess();
        }
    }, [loadStatus]);

    const [reportDownloading, setReportDownloading] = useState(false);
    const downloadReportCallback = useCallback(() => {
        setReportDownloading(true);

        downloadFailureReport().then(({ data, filename }) => {
            downloadBlob(data, filename);
            setReportDownloading(false);
        });
    }, []);

    return (
        <div>
            {loadStatus === 'idle' && healthInfo && (
                <>
                    <Toolbar className={styles.toolbar}>
                        <ToolbarButton
                            disabled={reportDownloading}
                            onClick={downloadReportCallback}
                            icon={
                                reportDownloading ? (
                                    <Spinner size="tiny" />
                                ) : (
                                    <ArrowDownloadRegular />
                                )
                            }
                        >
                            {t('Health_DownloadFailureReport')}
                        </ToolbarButton>
                    </Toolbar>
                    <div className={styles.section}>
                        <DepartureBoardTile className={styles.tile} />
                        <WelcomeCardTile className={styles.tile} />
                        <CrewTile className={styles.tile} />
                        <ConnectionsTile className={styles.tile} />
                        <BaggageTile className={styles.tile} />
                    </div>
                </>
            )}
            {loadStatus === 'loading' && (
                <Spinner className={styles.spinner} appearance="primary" />
            )}
        </div>
    );
};

export default HealthDashboardPage;
